import { useMemo } from 'react'

import { styled } from 'styled-components'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  isAuctionListing,
  isOfferingListing,
  isFulfillmentListing,
  isInvoiceSpotListing,
  type OrchestratedAuctionListingDetails,
  type OrchestratedOfferingListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { NestableAnalyticsProvider } from '@b-stock/fe-core/bstock-react'
import { Breakpoints } from '@b-stock/fe-core/design-system'

import { FeatureFlags } from '@/helpers/featureFlags'

import { AuctionActionColumn } from './auction/AuctionActionColumn'
import { AuctionActionColumnErrorBoundary } from './auction/AuctionActionColumnErrorBoundary'
import { FulfillmentActionColumn } from './fixedTerms/FulfillmentActionColumn'
import { InvoiceSpotActionColumn } from './fixedTerms/InvoiceSpotActionColumn'
import { OfferingActionColumn } from './offering/OfferingActionColumn'

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 21rem;
  width: 100%;

  > form {
    display: contents;
  }

  @media ${Breakpoints.max.medium} {
    min-width: 100%;
  }
`

export const ListingActionColumn = ({
  listing,
  initialBidAmount,
}: {
  listing:
    | OrchestratedOfferingListingDetails
    | OrchestratedAuctionListingDetails
  initialBidAmount?: string
}) => {
  const ccSurfacingEnabled = useFlag(FeatureFlags.ENABLE_CC_SURFACING)

  const ccAnalytics = useMemo(
    () => ({
      cc_tag_viewed: ccSurfacingEnabled && listing.isCreditCardEnabled === true,
      cc_eligibility_variant: ccSurfacingEnabled ? 'bundled' : 'control',
    }),
    [ccSurfacingEnabled, listing.isCreditCardEnabled]
  )

  const contents = useMemo(() => {
    if (isAuctionListing(listing)) {
      return (
        <AuctionActionColumnErrorBoundary listingId={listing._id}>
          <AuctionActionColumn
            listing={listing}
            initialBidAmount={initialBidAmount}
          />
        </AuctionActionColumnErrorBoundary>
      )
    }

    if (isOfferingListing(listing)) {
      return <OfferingActionColumn listing={listing} />
    }

    if (isFulfillmentListing(listing)) {
      return <FulfillmentActionColumn listing={listing} />
    }

    if (isInvoiceSpotListing(listing)) {
      return <InvoiceSpotActionColumn listing={listing} />
    }

    return null
  }, [listing, initialBidAmount])

  return (
    <NestableAnalyticsProvider value={ccAnalytics}>
      <Body data-testid="action-column">{contents}</Body>
    </NestableAnalyticsProvider>
  )
}
